/**
 * IMPORTS
 */
import {fromUnixTime} from 'date-fns';


/**
 * TYPES
 */
import {Intervals} from 'src/utils/getPeriods.d';


/**
 * CONSTANTS AND DEFINITIONS
 */

// dates in minutes
const ONE_MONTH = 31 * 24 * 60;

// days in minutes
const FIVE_DAYS = 5 * 24 * 60;
const FOUR_DAYS = 4 * 24 * 60;
const ONE_DAY = 24 * 60;
const TEN_DAYS = 10 * 24 * 60;
const THREE_DAYS = 3 * 24 * 60;
const TWENTY_DAYS = 20 * 24 * 60;
const TWO_DAYS = 2 * 24 * 60;

// hours in minutes
const FOUR_HOURS = 4 * 60;
const ONE_HOUR = 1 * 60;
const SIX_HOURS = 6 * 60;
const THREE_HOURS = 3 * 60;
const TWELVE_HOURS = 12 * 60;
const TWO_HOURS = 2 * 60;

// minutes
const FIFTEEN_MINUTES = 15;
const FIVE_MINUTES = 5;
const TEN_MINUTES = 10;
const THIRTY_MINUTES = 30;
const TWO_MINUTES = 2;
const ZERO_MINUTES = 0;

// available chart intervals
const INTERVALS: Intervals = [
    {
        range: {max: ONE_MONTH, min: TWENTY_DAYS},
        value: {desktop: TWO_DAYS, mobile: FOUR_DAYS},
    },
    {
        range: {max: TWENTY_DAYS, min: TEN_DAYS},
        value: {desktop: ONE_DAY, mobile: TWO_DAYS},
    },
    {
        range: {max: TEN_DAYS, min: FIVE_DAYS},
        value: {desktop: TWELVE_HOURS, mobile: ONE_DAY},
    },
    {
        range: {max: FIVE_DAYS, min: THREE_DAYS},
        value: {desktop: SIX_HOURS, mobile: TWELVE_HOURS},
    },
    {
        range: {max: THREE_DAYS, min: ONE_DAY},
        value: {desktop: THREE_HOURS, mobile: SIX_HOURS},
    },
    {
        range: {max: ONE_DAY, min: TWELVE_HOURS},
        value: {desktop: ONE_HOUR, mobile: THREE_HOURS},
    },
    {
        range: {max: TWELVE_HOURS, min: FOUR_HOURS},
        value: {desktop: THIRTY_MINUTES, mobile: TWO_HOURS},
    },
    {
        range: {max: FOUR_HOURS, min: TWO_HOURS},
        value: {desktop: TEN_MINUTES, mobile: ONE_HOUR},
    },
    {
        range: {max: TWO_HOURS, min: ONE_HOUR},
        value: {desktop: FIVE_MINUTES, mobile: TEN_MINUTES},
    },
    {
        range: {max: ONE_HOUR, min: ZERO_MINUTES},
        value: {desktop: TWO_MINUTES, mobile: FIFTEEN_MINUTES},
    },
];


/**
 * CODE
 */

/**
 * I define chart periods based on dates
 *
 * :param end: end date in unix time
 * :param start: start date in unix time
 *
 * :returns: periods
 */
function getPeriods(end: number, start: number): number[]
{
    // desktop screen width
    const DESKTOP_WIDTH = 650;

    // initialize periods
    const periods = [] as number[];

    // get screen width
    const screenWidth = window.innerWidth;

    // get dates in milliseconds
    const endDate = fromUnixTime(end).getTime();
    const startDate = fromUnixTime(start).getTime();

    // get dates range in milliseconds
    const range = Math.abs(startDate - endDate);

    // dates are equal: return empty list
    if (range === 0 || Number.isNaN(range) === true)
    {
        return [];
    }

    // get range in minutes
    const rangeInMinutes = range / 1000 / 60;

    // set is desktop device
    const isDesktop = screenWidth > DESKTOP_WIDTH;

    // get interval based on range
    const intervalOptions = INTERVALS.find(({range}) => (
        rangeInMinutes >= range.min && rangeInMinutes <= range.max
    ));

    // get value of interval
    const interval = intervalOptions.value[isDesktop ? 'desktop' : 'mobile'];

    // get interval in milliseconds
    const intervalInMs = interval * 60 * 1000;

    // initialize loop variables
    let stop = false;
    let date = startDate;

    // push start date to periods list
    periods.push(date);

    // fill periods list
    while (stop === false)
    {
        // increment date variable
        date += intervalInMs;

        // push to list
        periods.push(date);

        // date is in limit: stop loop
        if (date >= endDate)
        {
            stop = true;
        }
    }

    // return periods
    return periods;
}


/**
 * EXPORTS
 */
export default getPeriods;
